import { Fragment, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Container, Input, Label } from "reactstrap";
import { getReservationById, updateReservation, updateReservationStatus, getTables } from "../../utils/apiCalls";
import { errorToast } from "../../shared/Toast";
import { C, reportingGlobalCSS } from "../reporting/reportingStyles";
import { RCard, Badge, Loader } from "../reporting/ReportingShared";
import Breadcrumb from "../common/breadcrumb";

const statusBadge = (s) => {
  const map = { pending: { color: C.orange, bg: C.orangeLight }, confirmed: { color: C.blue, bg: C.blueLight }, seated: { color: C.purple, bg: C.purpleLight }, completed: { color: C.green, bg: C.greenLight }, cancelled: { color: C.textLight, bg: C.borderLight }, no_show: { color: C.coral, bg: C.coralLight } };
  return map[s] || { color: C.textMed, bg: C.borderLight };
};

const TRANSITIONS = {
  pending: [{ status: "confirmed", label: "Confirm", gradient: `linear-gradient(135deg, ${C.blue}, #A8B8FF)` }, { status: "cancelled", label: "Cancel", style: "outline" }],
  confirmed: [
    { status: "seated", label: "Seat Guest", gradient: `linear-gradient(135deg, ${C.coral}, #00bfa5)`, needsTable: true },
    { status: "cancelled", label: "Cancel", style: "outline" },
    { status: "no_show", label: "No Show", style: "outline" },
  ],
  seated: [{ status: "completed", label: "Complete", gradient: `linear-gradient(135deg, ${C.green}, #38F9D7)` }],
};

const fieldStyle = { borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 14 };
const labelStyle = { fontSize: 12, fontWeight: 600, color: C.textMed, marginBottom: 4 };

const EditReservation = () => {
  const { reservationId } = useParams();
  const navigate = useNavigate();
  const [reservation, setReservation] = useState(null);
  const [tables, setTables] = useState([]);
  const [selectedTableId, setSelectedTableId] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});

  useEffect(() => {
    const load = async () => {
      const data = await getReservationById(reservationId);
      if (data) {
        setReservation(data);
        setForm({ customerName: data.customerName || "", customerPhone: data.customerPhone || "", customerEmail: data.customerEmail || "", guestCount: data.guestCount || 1, reservationDate: data.reservationDate || "", startTime: data.startTime || "", endTime: data.endTime || "", specialOccasion: data.specialOccasion || "", notes: data.notes || "" });
        if (data.branchId) { const t = await getTables({ branchId: data.branchId }); setTables(t || []); }
      }
      setLoading(false);
    };
    load();
  }, [reservationId]);

  const handleChange = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    setSaving(true);
    const result = await updateReservation(reservationId, { ...form, guestCount: Number(form.guestCount) });
    if (result) setReservation((prev) => ({ ...prev, ...result }));
    setSaving(false);
  };

  const handleStatusChange = async (newStatus, needsTable) => {
    if (newStatus === "cancelled" || newStatus === "no_show") {
      if (!window.confirm(`Mark this reservation as "${newStatus}"?`)) return;
    }
    if (needsTable) {
      const tableId = selectedTableId || reservation?.tableId;
      if (!tableId) { errorToast("Please select a table to seat the guest"); return; }
      const result = await updateReservationStatus(reservationId, { status: newStatus, tableId: Number(tableId) });
      if (result) setReservation((prev) => ({ ...prev, ...result }));
    } else {
      const result = await updateReservationStatus(reservationId, { status: newStatus });
      if (result) setReservation((prev) => ({ ...prev, ...result }));
    }
  };

  if (loading) return <Fragment><Breadcrumb title="Edit Reservation" parent="Reservations" /><Container fluid><Loader /></Container></Fragment>;
  if (!reservation) return <Fragment><Breadcrumb title="Edit Reservation" parent="Reservations" /><Container fluid><RCard><p style={{ textAlign: "center", color: C.textLight }}>Reservation not found</p></RCard></Container></Fragment>;

  const transitions = TRANSITIONS[reservation.status] || [];
  const isTerminal = ["completed", "cancelled", "no_show"].includes(reservation.status);
  const sb = statusBadge(reservation.status);

  return (
    <Fragment>
      <style>{reportingGlobalCSS}</style>
      <Breadcrumb title="Edit Reservation" parent="Reservations" />
      <Container fluid className="rpt-wrap">
        {/* Status + Actions */}
        <RCard style={{ marginBottom: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: C.text }}>Status:</span>
              <Badge color={sb.color} bg={sb.bg}>{reservation.status}</Badge>
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              {transitions.some((t) => t.needsTable) && (
                <Input type="select" style={{ ...fieldStyle, maxWidth: 200 }} value={selectedTableId} onChange={(e) => setSelectedTableId(e.target.value)}>
                  <option value="">Select Table</option>
                  {tables.filter((t) => t.status === "available").map((t) => <option key={t.id} value={t.id}>{t.name} (#{t.tableNumber})</option>)}
                </Input>
              )}
              {transitions.map((t) => (
                <Button key={t.status} size="sm" style={t.style === "outline"
                  ? { background: C.white, border: `1px solid ${C.border}`, borderRadius: 8, color: C.coral, fontWeight: 600, fontSize: 12 }
                  : { background: t.gradient, border: "none", borderRadius: 8, fontWeight: 600, fontSize: 12 }
                } onClick={() => handleStatusChange(t.status, t.needsTable)}>
                  {t.label}
                </Button>
              ))}
            </div>
          </div>
        </RCard>

        {/* Edit Form */}
        <RCard>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <div><Label style={labelStyle}>Customer Name</Label><Input value={form.customerName} onChange={(e) => handleChange("customerName", e.target.value)} disabled={isTerminal} style={fieldStyle} /></div>
            <div><Label style={labelStyle}>Phone</Label><Input value={form.customerPhone} onChange={(e) => handleChange("customerPhone", e.target.value)} disabled={isTerminal} style={fieldStyle} /></div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, marginTop: 16 }}>
            <div><Label style={labelStyle}>Guest Count</Label><Input type="number" min="1" value={form.guestCount} onChange={(e) => handleChange("guestCount", e.target.value)} disabled={isTerminal} style={fieldStyle} /></div>
            <div><Label style={labelStyle}>Date</Label><Input type="date" value={form.reservationDate} onChange={(e) => handleChange("reservationDate", e.target.value)} disabled={isTerminal} style={fieldStyle} /></div>
            <div><Label style={labelStyle}>Start Time</Label><Input type="time" value={form.startTime} onChange={(e) => handleChange("startTime", e.target.value)} disabled={isTerminal} style={fieldStyle} /></div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginTop: 16 }}>
            <div><Label style={labelStyle}>Special Occasion</Label><Input value={form.specialOccasion} onChange={(e) => handleChange("specialOccasion", e.target.value)} disabled={isTerminal} style={fieldStyle} /></div>
            <div><Label style={labelStyle}>Notes</Label><Input type="textarea" value={form.notes} onChange={(e) => handleChange("notes", e.target.value)} disabled={isTerminal} style={{ ...fieldStyle, minHeight: 60 }} /></div>
          </div>
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 24 }}>
            <Button style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 8, color: C.textMed, fontWeight: 600 }} onClick={() => navigate("/reservations/list")}>Back</Button>
            {!isTerminal && (
              <Button style={{ background: `linear-gradient(135deg, ${C.coral}, #00bfa5)`, border: "none", borderRadius: 8, fontWeight: 600 }} onClick={handleSave} disabled={saving}>
                {saving ? "Saving..." : "Save Changes"}
              </Button>
            )}
          </div>
        </RCard>
      </Container>
    </Fragment>
  );
};

export default EditReservation;
